import { useEffect } from 'react';
const PIXEL_ID = '4012054475762277';
type FbqWindow = Window & {
  fbq?: ((...args: unknown[]) => void) & {
    queue?: unknown[];
    loaded?: boolean;
  };
  _fbq?: unknown;
  __metaPixelPageView?: boolean;
};

/**
 * Meta Pixel base code, injected once as early as possible on load.
 *
 * PageView must fire EXACTLY ONCE per page load. Three things could double it,
 * and all three are guarded here:
 *  1. React 18 StrictMode mounting effects twice — the module-level flag below.
 *  2. Any remount of this component — the window-level flag, which survives it.
 *  3. The pixel library already being present (e.g. injected by the host page)
 *     — in that case we reuse the existing fbq instead of re-initializing.
 *
 * NOTE: Magic Patterns has no editable index.html <head>, so the official
 * snippet is injected at runtime. The pixel ID and script structure are kept
 * verbatim.
 */
let hasRun = false;
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as FbqWindow;

    // Guards 1 and 2: never run the setup twice, in any lifecycle.
    if (hasRun || w.__metaPixelPageView) return;
    hasRun = true;
    w.__metaPixelPageView = true;

    // Guard 3: the library is already on the page — just init and track once.
    if (typeof w.fbq === 'function') {
      w.fbq('init', PIXEL_ID);
      w.fbq('track', 'PageView');
      return;
    }

    // --- Meta Pixel Code (script) ---
    const script = document.createElement('script');
    script.innerHTML = `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${PIXEL_ID}');
fbq('track', 'PageView');`;
    document.head.appendChild(script);

    // --- Meta Pixel Code (noscript fallback) ---
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, []);
  return null;
}